import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

function useEventListener<T extends HTMLElement = HTMLDivElement>(
  eventName: keyof WindowEventMap,
  handler: (event: KeyboardEvent) => void,
  element?: RefObject<T>,
): void {
  // Create a ref that stores handler
  const savedHandler = useRef<(event: KeyboardEvent) => void>();

  useEffect(() => {
    // Define the listening target
    const targetElement: T | Window = element?.current || window;
    if (!(targetElement && targetElement.addEventListener)) {
      return;
    }

    // Update saved handler if necessary
    if (savedHandler.current !== handler) {
      savedHandler.current = handler;
    }

    // Create event listener that calls handler function stored in ref
    const eventListener = (event: Event) => {
      // TODO: Rewrite to avoid extra boolean cast
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!savedHandler?.current) {
        // TODO: Use run-time type testing (e.g. io-ts, zod) to test for assertions instead of making them
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        savedHandler.current(event as KeyboardEvent);
      }
    };

    targetElement.addEventListener(eventName, eventListener);

    // Remove event listener on cleanup
    // TODO: Review consistent-return documentation and rewrite to avoid this disable
    // eslint-disable-next-line consistent-return
    return () => {
      targetElement.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element, handler]);
}

export default useEventListener;
